import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatErr, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, Package, CheckCircle, Trash, X, Paperclip } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useBackClose } from "@/hooks/use-back-close";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("en-IN") : "-";

const receiptUrl = (path) => {
  if (!path) return "";
  return path.startsWith("http") ? path : `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const PERIOD_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "custom", label: "Custom Date Range" },
];

/** Resolve a period key into { date_from, date_to } (YYYY-MM-DD, inclusive), or null for "all". */
function resolvePeriodRange(period, customStart, customEnd) {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  switch (period) {
    case "today": {
      const t = startOfDay(now);
      return { date_from: toYMD(t), date_to: toYMD(t) };
    }
    case "yesterday": {
      const y = new Date(startOfDay(now));
      y.setDate(y.getDate() - 1);
      return { date_from: toYMD(y), date_to: toYMD(y) };
    }
    case "7d": {
      const start = new Date(startOfDay(now));
      start.setDate(start.getDate() - 6);
      return { date_from: toYMD(start), date_to: toYMD(now) };
    }
    case "this_month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { date_from: toYMD(start), date_to: toYMD(now) };
    }
    case "last_month": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { date_from: toYMD(start), date_to: toYMD(end) };
    }
    case "custom": {
      if (!customStart || !customEnd) return null;
      return { date_from: customStart, date_to: customEnd };
    }
    default:
      return null;
  }
}

export default function DeliveredOrders() {
  const { user } = useAuth();
  // Sales Reps (role="user") cannot delete records — Admin/Owner only. This
  // flag hides the Delete controls (and the row-select checkboxes that only
  // exist to bulk-delete) for Sales Reps, everywhere on this page.
  const canManage = user?.role === "owner" || user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [singleDeleteTarget, setSingleDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewReceiptRow, setViewReceiptRow] = useState(null); // row whose receipt is being previewed in-app
  const pageSize = 10;

  // Phone/browser back button closes whichever dialog is open instead of
  // navigating the page away (which used to jump out of the app).
  useBackClose(!!singleDeleteTarget, () => setSingleDeleteTarget(null));
  useBackClose(bulkDeleteOpen, () => setBulkDeleteOpen(false));
  useBackClose(!!viewReceiptRow, () => setViewReceiptRow(null));

  const range = useMemo(
    () => resolvePeriodRange(period, customStart, customEnd),
    [period, customStart, customEnd]
  );

  const load = useCallback(async () => {
    try {
      const params = { status: "delivered", search, page, page_size: pageSize, sort_by: "delivered_at" };
      if (range) {
        params.date_from = range.date_from;
        params.date_to = range.date_to;
        params.date_field = "delivered_at";
      }
      const { data } = await api.get("/sales", { params });
      setRows(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(formatErr(e)); }
  }, [search, page, range]);

  useEffect(() => { load(); }, [load]);

  // Clear selection whenever the visible list changes shape (filter, period, or page change).
  useEffect(() => { setSelected(new Set()); }, [search, period, customStart, customEnd, page]);

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.has(r.invoice_no));
  const someVisibleSelected = rows.some((r) => selected.has(r.invoice_no));

  const toggleAll = () => {
    setSelected((prev) => {
      if (allVisibleSelected) return new Set();
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.invoice_no));
      return next;
    });
  };

  const toggleOne = (invoice_no) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(invoice_no)) next.delete(invoice_no);
      else next.add(invoice_no);
      return next;
    });
  };

  const doSingleDelete = async () => {
    if (!singleDeleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/sales/${encodeURIComponent(singleDeleteTarget.invoice_no)}`);
      toast.success(`Deleted invoice ${singleDeleteTarget.invoice_no}`);
      setSingleDeleteTarget(null);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(singleDeleteTarget.invoice_no);
        return next;
      });
      // If we just deleted the last row on a page beyond page 1, step back a page; otherwise reload in place.
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const invoice_numbers = Array.from(selected);
      const { data } = await api.post("/sales/bulk-delete", { invoice_numbers });
      toast.success(`Successfully deleted ${data.deleted} order${data.deleted === 1 ? "" : "s"}`);
      setBulkDeleteOpen(false);
      setSelected(new Set());
      if (rows.length === invoice_numbers.length && page > 1) setPage((p) => p - 1);
      else load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="delivered-page">
      <div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Delivered Orders</h1>
        <p className="text-sm text-muted-foreground mt-1">All completed deliveries with WhatsApp status.</p>
      </div>

      <div className="brut-card">
        <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search Invoice No, Party, Mobile"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-signal/40"
              data-testid="delivered-search-input"
            />
          </div>

          <Select value={period} onValueChange={(v) => { setPeriod(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[180px] h-10" data-testid="delivered-period-filter">
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {period === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStart}
                onChange={(e) => { setCustomStart(e.target.value); setPage(1); }}
                data-testid="delivered-period-start"
                className="h-10 px-3 rounded-md border border-border bg-background text-sm"
              />
              <span className="text-muted-foreground text-xs">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => { setCustomEnd(e.target.value); setPage(1); }}
                data-testid="delivered-period-end"
                className="h-10 px-3 rounded-md border border-border bg-background text-sm"
              />
            </div>
          )}

          {(search || period !== "all") && (
            <button
              onClick={() => { setSearch(""); setPeriod("all"); setCustomStart(""); setCustomEnd(""); setPage(1); }}
              className="h-10 px-3 rounded-md border border-border text-sm hover:bg-muted"
              data-testid="delivered-clear-filters-button"
            >
              Clear
            </button>
          )}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-xs uppercase tracking-[0.05em] text-muted-foreground">
                {canManage && (
                  <th className="text-left px-4 py-3 font-semibold w-10">
                    <Checkbox
                      checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                      onCheckedChange={toggleAll}
                      disabled={rows.length === 0}
                      aria-label="Select all delivered orders on this page"
                      data-testid="delivered-select-all-checkbox"
                    />
                  </th>
                )}
                <th className="text-left px-4 py-3 font-semibold">Voucher Type</th>
                <th className="text-left px-4 py-3 font-semibold">Invoice No</th>
                <th className="text-left px-4 py-3 font-semibold">Salesman</th>
                <th className="text-left px-4 py-3 font-semibold">Transporter</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Party</th>
                <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                <th className="text-right px-4 py-3 font-semibold">Invoice Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Delivered At</th>
                <th className="text-center px-4 py-3 font-semibold">WhatsApp</th>
                <th className="text-center px-4 py-3 font-semibold">Receipt</th>
                {canManage && <th className="text-right px-4 py-3 font-semibold">Action</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={canManage ? 13 : 11} className="text-center py-16 text-muted-foreground">
                  <Package size={40} weight="duotone" className="mx-auto mb-3 opacity-40" />
                  No delivered orders {period !== "all" || search ? "match your filters" : "yet"}
                </td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className={`border-t border-border hover:bg-muted/40 ${selected.has(r.invoice_no) ? "bg-signal/5" : ""}`}
                  data-testid={`delivered-row-${r.invoice_no}`}
                >
                  {canManage && (
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selected.has(r.invoice_no)}
                        onCheckedChange={() => toggleOne(r.invoice_no)}
                        aria-label={`Select invoice ${r.invoice_no}`}
                        data-testid={`delivered-row-checkbox-${r.invoice_no}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium">{r.voucher_type}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.invoice_no}</td>
                  <td className="px-4 py-3">{r.salesman || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3">{r.transporter || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.date}</td>
                  <td className="px-4 py-3">{r.party_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.party_mobile}</td>
                  <td className="px-4 py-3 text-right font-mono">{inr(r.invoice_amount)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDT(r.delivered_at)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border border-signal/40 bg-signal/10 text-signal dark:text-signal">
                      <CheckCircle size={12} weight="fill" /> {r.whatsapp_status || "sent"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {r.receipt_file_path ? (
                      <button
                        type="button"
                        onClick={() => setViewReceiptRow(r)}
                        data-testid={`delivered-view-receipt-${r.invoice_no}`}
                        className="inline-flex items-center gap-1 text-xs text-signal hover:underline"
                      >
                        <Paperclip size={14} /> View Receipt
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSingleDeleteTarget(r)}
                        data-testid={`delivered-delete-btn-${r.invoice_no}`}
                        aria-label={`Delete invoice ${r.invoice_no}`}
                        title="Delete"
                        className="h-8 w-8 rounded-md inline-flex items-center justify-center border border-signal/40 bg-signal/10 text-signal hover:bg-signal/20 transition-colors"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list — shown below md breakpoint instead of the table */}
        <div className="md:hidden divide-y divide-border">
          {rows.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <Package size={40} weight="duotone" className="mx-auto mb-3 opacity-40" />
              No delivered orders {period !== "all" || search ? "match your filters" : "yet"}
            </div>
          )}
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`delivered-card-${r.invoice_no}`}
              className={`p-4 space-y-3 ${selected.has(r.invoice_no) ? "bg-signal/5" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  {canManage && (
                    <div className="pt-0.5">
                      <Checkbox
                        checked={selected.has(r.invoice_no)}
                        onCheckedChange={() => toggleOne(r.invoice_no)}
                        aria-label={`Select invoice ${r.invoice_no}`}
                        data-testid={`delivered-row-checkbox-mobile-${r.invoice_no}`}
                      />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-muted-foreground">{r.invoice_no}</div>
                    <div className="font-semibold leading-snug break-words line-clamp-2">{r.party_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{r.voucher_type} • {r.date}</div>
                  </div>
                </div>
                <div className="font-mono font-semibold text-right whitespace-nowrap shrink-0">{inr(r.invoice_amount)}</div>
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Mobile</div>
                  <div className="font-mono text-[13px]">{r.party_mobile}</div>
                </div>
                {r.salesman && (
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Salesman</div>
                    <div className="text-[13px] leading-snug break-words">{r.salesman}</div>
                  </div>
                )}
                {r.transporter && (
                  <div className="col-span-2 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Transporter</div>
                    <div className="text-[13px] leading-snug break-words">🚚 {r.transporter}</div>
                  </div>
                )}
                <div className="col-span-2 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Delivered</div>
                  <div className="text-[13px] leading-snug">{fmtDT(r.delivered_at)}</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border border-signal/40 bg-signal/10 text-signal dark:text-signal">
                  <CheckCircle size={12} weight="fill" /> {r.whatsapp_status || "sent"}
                </span>
                {r.receipt_file_path ? (
                  <button
                    type="button"
                    onClick={() => setViewReceiptRow(r)}
                    data-testid={`delivered-view-receipt-mobile-${r.invoice_no}`}
                    className="inline-flex items-center gap-1 text-xs text-signal"
                  >
                    <Paperclip size={14} /> View Receipt
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground">No receipt</span>
                )}
              </div>

              {canManage && (
                <div>
                  <button
                    onClick={() => setSingleDeleteTarget(r)}
                    data-testid={`delivered-delete-btn-mobile-${r.invoice_no}`}
                    className="h-9 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 border border-signal/40 bg-signal/10 text-signal"
                  >
                    <Trash size={14} weight="bold" /> Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-border flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} delivered</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="h-8 px-3 rounded-md border border-border disabled:opacity-40 hover:bg-muted">Prev</button>
            <span className="font-mono text-xs">Page {page} / {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}
              className="h-8 px-3 rounded-md border border-border disabled:opacity-40 hover:bg-muted">Next</button>
          </div>
        </div>
      </div>

      {/* Floating bulk action bar — appears once 1+ rows are selected */}
      {canManage && selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 px-5 py-3 rounded-md border border-border bg-card shadow-lg max-w-[calc(100vw-2rem)] flex-wrap justify-center"
          data-testid="delivered-bulk-action-bar"
        >
          <span className="text-sm font-medium">
            {selected.size} item{selected.size === 1 ? "" : "s"} selected
          </span>
          <button
            onClick={() => setBulkDeleteOpen(true)}
            data-testid="delivered-bulk-delete-button"
            className="h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
          >
            <Trash size={15} weight="bold" /> Delete Selected
          </button>
          <button
            onClick={() => setSelected(new Set())}
            data-testid="delivered-bulk-clear-button"
            aria-label="Clear selection"
            className="h-9 w-9 rounded-md border border-border inline-flex items-center justify-center hover:bg-muted"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {/* Single delete confirmation */}
      <AlertDialog open={!!singleDeleteTarget} onOpenChange={(o) => !o && setSingleDeleteTarget(null)}>
        <AlertDialogContent data-testid="delivered-single-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice <strong>{singleDeleteTarget?.invoice_no}</strong>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delivered-single-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doSingleDelete}
              disabled={busy}
              data-testid="delivered-single-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? "Deleting..." : "Confirm Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => !o && setBulkDeleteOpen(false)}>
        <AlertDialogContent data-testid="delivered-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Orders</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selected.size}</strong> selected delivered order{selected.size === 1 ? "" : "s"}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delivered-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulkDelete}
              disabled={busy}
              data-testid="delivered-bulk-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? "Deleting..." : "Confirm Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receipt preview — opens in-app instead of a new browser tab, so the
          phone's back button just closes this and returns to the list. */}
      <Dialog open={!!viewReceiptRow} onOpenChange={(o) => !o && setViewReceiptRow(null)}>
        <DialogContent data-testid="delivered-view-receipt-dialog">
          <DialogHeader>
            <DialogTitle>Delivery Receipt — {viewReceiptRow?.invoice_no}</DialogTitle>
            <DialogDescription>{viewReceiptRow?.party_name}</DialogDescription>
          </DialogHeader>
          {viewReceiptRow?.receipt_file_path && (
            /\.pdf($|\?)/i.test(viewReceiptRow.receipt_file_path) ? (
              <iframe
                src={receiptUrl(viewReceiptRow.receipt_file_path)}
                title="Delivery receipt"
                className="w-full h-[70vh] rounded-md border border-border"
              />
            ) : (
              <img
                src={receiptUrl(viewReceiptRow.receipt_file_path)}
                alt="Delivery receipt"
                className="w-full max-h-[70vh] object-contain rounded-md border border-border"
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}